import React from 'react';
import { Alert, Descriptions, Table, Tag, Typography } from 'antd';

import { summaryFacts, summaryTables, warningsOf } from './batchDetail.js';

const { Text } = Typography;

/**
 * The drawer's class summary (design 2026-10-02 section 8): what the class's
 * parser counted in the file, as it wrote it at the scan - its plain facts
 * (batchDetail.js summaryFacts) and its lists of records as small tables (a
 * NAV file's per-fund figures, say: summaryTables). Nothing when the summary
 * has neither.
 *
 * @param {{ summary: object|null }} props
 */
export const ClassSummary = ({ summary }) => {
    const facts = summaryFacts(summary);
    const tables = summaryTables(summary);
    if (!facts.length && !tables.length) return null;
    return (
        <div className="uploads-class-summary">
            {facts.length > 0 && (
                <Descriptions size="small" column={2} bordered title="Summary">
                    {facts.map((f) => <Descriptions.Item key={f.key} label={f.label}>{f.value}</Descriptions.Item>)}
                </Descriptions>
            )}
            {tables.map((t) => (
                <div key={t.key} className="uploads-summary-table">
                    <h4 className="panel-title">{t.label}</h4>
                    <Table rowKey="key" size="small" pagination={false} dataSource={t.rows} scroll={{ x: 'max-content' }}
                        columns={t.columns.map((c) => ({ title: c.label, dataIndex: c.key, key: c.key, render: (v) => (v === null || v === undefined ? '—' : String(v)) }))} />
                    {t.more > 0 && <Text type="secondary">{t.more} more not listed.</Text>}
                </div>
            ))}
        </div>
    );
};

const ITEM_COLUMNS = [
    { title: 'Row', dataIndex: 'rowNo', key: 'rowNo', width: 70, render: (v) => (v ? v : 'file') },
    { title: 'Rule', dataIndex: 'rule', key: 'rule', width: 200, render: (v) => <Tag color="gold">{v}</Tag> },
    { title: 'Column', dataIndex: 'column', key: 'column', width: 160, render: (v) => v ?? '—' },
    { title: 'Value', dataIndex: 'value', key: 'value', render: (v) => (v === null || v === undefined ? '—' : <Text code>{v}</Text>) }
];

/**
 * A VALIDATED batch's warnings (IP-103: in its summary, so the checker's
 * code covers them): the count of each rule with its first message, and the
 * first warnings listed. When there are more than the summary lists, the
 * rest are on the rows - "Rows with findings only" pages them.
 *
 * @param {{ summary: object|null }} props
 */
export const WarningsBlock = ({ summary }) => {
    const warnings = warningsOf(summary);
    if (!warnings) return null;
    return (
        <Alert
            type="warning"
            showIcon
            className="auth-alert uploads-warnings"
            message={warnings.total + ' warning' + (warnings.total === 1 ? '' : 's') + ': the file can still be accepted'}
            description={(
                <div>
                    <ul className="uploads-warning-rules">
                        {warnings.rules.map((r) => (
                            <li key={r.rule}><Tag color="gold">{r.rule}</Tag>{' × ' + r.count}{r.message ? ' - ' + r.message : ''}</li>
                        ))}
                    </ul>
                    {warnings.items.length > 0 && (
                        <Table rowKey="key" size="small" columns={ITEM_COLUMNS} dataSource={warnings.items} pagination={{ pageSize: 10, hideOnSinglePage: true }} />
                    )}
                    {warnings.more > 0 && (
                        <Text type="secondary">
                            {warnings.more} more are on the rows: turn on "Rows with findings only" below to page through every one.
                        </Text>
                    )}
                </div>
            )}
        />
    );
};
